import QuestionAnswerComponent, {Question} from "../../../../components/help/QuestionAnswerComponent.tsx";
import {Link} from "react-router-dom";
import {imageMap} from "../../../../images/imageMap.ts";
import RelatedArticlesComponent from "../../../../components/help/RelatedArticlesComponent.tsx";
import Routing from "../../../../Routing.ts";
import OrderAddressSearchComponent from "../../../../components/order/OrderAddressSearchComponent.tsx";

const questions: Question[] = [
    {
        question: "Hvad står wifi for?",
        answer: <p>Wifi står for &ldquo;wireless fidelity&rdquo; (trådløst internet).</p>,
    },
    {
        question: "Hvad er forskellen på wifi og trådløst internet?",
        answer: <p>
            Trådløst internet er enhver form for internetforbindelse, der ikke kræver fysiske kabler for at forbinde enheder til internettet.
            Wifi er en type trådløst internet &ndash; der findes også andre typer såsom mobilt og satellit.
        </p>,
    },
    {
        question: "Hvad er forskellen på wifi fra en fastnet-router og wifi fra 5G-internet?",
        answer: <>
            <p>
                Wifi som er tilsluttet fastnet-forbindelser såsom <Link to={Routing.Broadband.Fiber}>fibernet</Link> og <Link to={Routing.Broadband.Coax}>internet via kabel-tv-stik</Link> er internet gennem en router, som du kobler dine enheder til.
                Routerne til fastnetforbindelser er tilsluttet et strømstik og et internetstik (fra enten et fiberstik eller et kabel-tv-stik).
                Vores 5G-routere bruger simkort og skal kun tilkobles et strømstik &ndash; ikke et internetstik.
            </p>
            <p>
                <Link to="/bredbaand/mobilt-bredbaand">Mobilt bredbånd/5G-internet</Link> er også internet gennem en router &ndash; den kræver dog ikke et internetstik.
                Det mobile bredbånd tilsluttes internettet via 4G- eller 5G-forbindelser, så du kan komme på nettet via mobilmaster.
            </p>
        </>
    },
    {
        question: "Hvad er WLAN?",
        answer: <p>
            WLAN står for &ldquo;Wireless Local Area Network&rdquo;.
            WLAN er en betegnelse for ethvert trådløst lokalnetværk, der forbinder enheder inden for et begrænset område (f.eks. et hjem) ved hjælp af radiobølger.
            Wifi er en type WLAN.
        </p>,
    },
    {
        question: "Hvad er et access point?",
        answer: <p>
            Et access point er en betegnelse for en router med wifi.
            Du bruger et access point til at få adgang til et netværk.
        </p>,
    },
    {
        question: "Hvordan får jeg bedre wifi i hjemmet?",
        answer: <>
            <p>
                Det er vigtigt, at din router er <Link to={"/hjaelp/internet-og-wifi/placering-af-router"}>placeret optimalt i din bolig</Link>.
                Dit wifi-signal kan nemlig blive påvirket af mange forskellige ting i din bolig, f.eks. betonvægge og køkkenmaskiner.
            </p>
            <p>
                Du kan også læse mere om, hvad du kan <Link to="/hjaelp/internet-og-wifi/forventninger-til-wifi">forvente af dit wifi</Link>.
            </p>
        </>,
    },
    {
        question: "Hvordan skifter jeg navn og adgangskode til min wifi?",
        answer: <p>
            Det er super nemt &ndash; du kan læse hvordan <Link to="/hjaelp/internet-og-wifi/skift-navn-og-kode-paa-wifi">lige her.</Link>
        </p>
    },
]

export default function InternetAndWifiArticle() {
    return (
        <>
            <article className="help-article-content">
                <div className="article">
                    <div className="text-box text-box--gray">
                        <p>
                            Internet er et globalt netværk, og wifi er en trådløs forbindelse til internettet eller et andet lokalt netværk.
                        </p>
                    </div>
                </div>

                <div className="article">
                    <div className="paragraph">
                        <h2>
                            Hvad er wifi?
                        </h2>

                        <h3>
                            Et trådløst lokalnetværk
                        </h3>
                        <p>
                            Wifi er en teknologi, der gør det muligt for enheder at oprette en trådløs forbindelse til et lokalnetværk (LAN), typisk ved brug af en router.
                        </p>

                        <h3>
                            Afgrænset rækkevidde
                        </h3>
                        <p>
                            Et wifi-netværk dækker normalt et område som f.eks. et hjem eller en lille virksomhed.
                            Rækkevidden påvirkes af forhindringer som vægge og møbler.
                        </p>

                        <h3>
                            Kræver en router
                        </h3>
                        <p>
                            For at du kan bruge et wifi-netværk skal du have et access point (typisk en router) som er tilsluttet internettet.
                            Routeren sender trådløse signaler, som dine enheder opfanger for at oprette forbindelse til netværket.
                        </p>

                        <h3>
                            Giver adgang til internettet
                        </h3>
                        <p>
                            Wifi i sig selv giver ikke adgang til internettet &ndash; det kræver også en router.
                            Dine mobiltelefoner og computere kan bruge wifi på en router, som er forbundet til internettet via en internetudbyder, f.eks. os.
                        </p>
                    </div>
                </div>

                <div className="article article--connect">
                    <div className="paragraph">
                        <h2>
                            Hvad er internettet?
                        </h2>

                        <h3>
                            Et globalt netværk
                        </h3>
                        <p>
                            Internettet er et globalt netværk af computere og andre enheder, der kommunikerer med hinanden.
                        </p>

                        <h3>
                            Tilslutningsmetoder
                        </h3>
                        <p>
                            Du kan oprette forbindelse til internettet via forskellige metoder såsom fiber, kabel-tv, telefonstik, mobilnetværk (4G eller 5G) og satellitforbindelser.
                        </p>

                        <h3>
                            Serviceudbydere
                        </h3>
                        <p>
                            For at få adgang til internettet, har du brug for en internetudbyder (ISP), som leverer forbindelsen til din bolig eller virksomhed.
                        </p>
                    </div>
                </div>

                <div className="article">
                    <img
                        className="hide-on-small-devices"
                        src={imageMap.help.network.internet_to_devices_desktop}
                        alt="Illustration af hvordan internet og wifi er forbundet og dernæst tilkoblet enheder."
                    />
                    <img
                        className="hide-on-large-devices"
                        src={imageMap.help.network.internet_to_devices_mobile}
                        alt="Illustration af hvordan internet og wifi er forbundet og dernæst tilkoblet enheder."
                    />
                </div>

                <div className="article article--connect">
                    <h2>
                        Spørgsmål omkring wifi og trådløst internet
                    </h2>
                    <QuestionAnswerComponent questions={questions}/>
                </div>

                <div className="article">
                    <div className="paragraph">
                        <h2>
                            Kan jeg få internet hos jer?
                        </h2>
                        <p>
                            Ja!
                            Du kan købe internet via <Link to={Routing.Broadband.Fiber}>fibernet</Link>, <Link to={Routing.Broadband.TDC5G}>5G-internet</Link> eller <Link to={Routing.Broadband.Coax}>kabel-tv</Link> hos os.
                            Der følger en gratis lånerouter med dit abonnement, som giver dig trådløst internet til hjemmet.
                        </p>
                        <p>
                            Slå din adresse op her og find det rigtige produkt til dig!
                        </p>
                        <OrderAddressSearchComponent/>
                    </div>
                </div>
            </article>

            <RelatedArticlesComponent
                articles={[
                    {
                        "category": "internet-og-wifi",
                        "article": "forventninger-til-wifi",
                        "name": "Hvad kan jeg forvente af mit wifi?"
                    },
                    {
                        "category": "internet-og-wifi",
                        "article": "forskel-paa-signal-frekvenser",
                        "name": "Forskellen på 2,4 GHz og 5 GHz"
                    }
                ]}
            />
        </>
    )
}
